import { useState, useEffect } from "react";
import { Paiement, Contrat, PaiementTemplate } from "../../model/Model";
import { getContratsActifs, getPaiementById } from "../../service/paiements/paiements.service";
import { getPaiementTemplates } from "../../service/paiements/paiement-templates.service";

interface AjouterPaiementPopupProps {
    ajouterModifier: (paiement: Paiement) => Promise<void>;
    onClose: () => void;
    paiementId?: number;
}

export function AjouterPaiementPopup({ ajouterModifier, onClose, paiementId }: AjouterPaiementPopupProps) {


    const [paiement, setPaiement] = useState<Paiement>({
        contratId: 0,
        templateId: undefined,
        montant: 0,
        dateDePaiement: new Date().toISOString().split('T')[0],
        dateDeDebutEffet: "",
        dateDeFinEffet: "",
        modeDePaiement: 'VIREMENT',
        description: ""
    });

    const [contrats, setContrats] = useState<Contrat[]>([]);
    const [templates, setTemplates] = useState<PaiementTemplate[]>([]);

    useEffect(() => {
        async function fetchData() {
            const contratsData = await getContratsActifs();
            

            setContrats(contratsData);
            

            const templatesData = await getPaiementTemplates();
            setTemplates(templatesData);
        }
        fetchData();
    }, []);

    useEffect(() => {
        async function fetchPaiement() {
            if (!paiementId) return;
            const data = await getPaiementById(paiementId);
            if (data) {
                setPaiement(data);
            }
        }
        fetchPaiement();
    }, [paiementId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log("Submitting paiement:", paiement);
        await ajouterModifier(paiement);
        // Reset form
        setPaiement({
            paiementId: 0,
            contratId: 0,
            templateId: undefined,
            montant: 0,
            dateDePaiement: new Date().toISOString().split('T')[0],
            dateDeDebutEffet: "",
            dateDeFinEffet: "",
            modeDePaiement: 'VIREMENT',
            description: ""
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;


        console.log(`Input changed: ${name} = ${value}`);

        setPaiement(prev => ({
            ...prev,
            [name]: name === 'contratId' || name === 'montant' || name === 'templateId'
                ? Number(value) || undefined
                : value
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {paiementId ? "Modifier le paiement" : "Enregistrer un nouveau paiement"}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contrat *
                        </label>
                        <select
                            name="contratId"
                            value={paiement.contratId}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value={0}>Sélectionnez un contrat</option>

                            {contrats && contrats.map((contrat) => (
                                <option key={contrat.contratId} value={contrat.contratId}>
                                    {contrat.bienInfo} - ({contrat.locataireInfo})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Template de paiement
                            <span className="text-xs text-gray-500 ml-2">(optionnel - pré-remplit la description)</span>
                        </label>
                        <select
                            name="templateId"
                            value={paiement.templateId || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                            <option value="">Aucun template</option>
                            {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.nom} ({template.type})
                                </option>
                            ))}
                        </select>
                        {templates.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                                💡 Créez des templates dans l'onglet "Templates de paiements"
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Montant (€) *
                        </label>
                        <input
                            type="number"
                            name="montant"
                            value={paiement.montant}
                            onChange={handleInputChange}
                            required
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0.00"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date de paiement *
                        </label>
                        <input
                            type="date"
                            name="dateDePaiement"
                            value={paiement.dateDePaiement}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date début période *
                            </label>
                            <input
                                type="date"
                                name="dateDeDebutEffet"
                                value={paiement.dateDeDebutEffet}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date fin période *
                            </label>
                            <input
                                type="date"
                                name="dateDeFinEffet"
                                value={paiement.dateDeFinEffet}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mode de paiement *
                        </label>
                        <select
                            name="modeDePaiement"
                            value={paiement.modeDePaiement}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="VIREMENT">Virement</option>
                            <option value="ESPECE">Espèce</option>
                            <option value="CHEQUE">Chèque</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={paiement.description}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Notes ou détails supplémentaires..."
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition duration-200"
                        >
                            Annuler
                        </button>
                        {paiementId ? (
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition duration-200"
                            >
                                Modifier le paiement
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition duration-200"
                            >
                                Enregistrer le paiement
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
